/**
 * ViewPdfLink — Composant unifié de lien "Voir le PDF" iPhone-safe.
 *
 * BLOC 2026-10-18 20h55 — Refonte complète (anti-`about:blank`).
 *
 * Rend un `<a href={signedUrl} target="_blank" rel="noopener noreferrer">`
 * natif. Zéro `window.open`, zéro `location.href` JS. Le lien est
 * pré-fetché via `useSignedPdfHref` et devient cliquable UNIQUEMENT
 * quand `href` est disponible.
 *
 * États visuels :
 *   - loading : "Préparation…" + spinner, aria-disabled, tabindex=-1
 *   - error   : disabled + tooltip FR "PDF indisponible pour le moment"
 *   - ready   : lien actif, navigation native
 *
 * Variantes `as` :
 *   - "dropdown-item" (défaut)       → wrappé dans `<a>` compatible Radix DropdownMenuItem
 *   - "button"                        → bouton primaire (ex. PreviewPdfBtn éditeur devis)
 *   - "button-outline"                → bouton outline (ex. récap chantier)
 *
 * API :
 *   <ViewPdfLink
 *     entityType="invoice"
 *     entityId={inv.id}
 *     enabled={menuOpen}
 *     label="Voir le PDF"
 *     icon={<FileDown />}
 *     testid={`invoice-view-pdf-${inv.id}`}
 *     as="dropdown-item"
 *   />
 */
import React from "react";
import { FileDown, Loader2 } from "lucide-react";
import { useSignedPdfHref } from "@/hooks/useSignedPdfHref";
import { Button } from "@/components/ui/button";

export function ViewPdfLink({
  entityType,
  entityId,
  enabled = true,
  label = "Voir le PDF",
  icon,
  testid,
  as = "dropdown-item",
  className = "",
  disabled = false,
  title,
}) {
  const { href, loading, error } = useSignedPdfHref(entityType, entityId, { enabled });
  const Icon = icon || <FileDown className="h-4 w-4" />;
  const isReady = !!href && !loading && !error && !disabled;
  const displayLabel = loading ? "Préparation…" : error ? "PDF indisponible" : label;
  const tooltip = error
    ? "Le PDF n'est pas disponible pour le moment. Rouvrez le menu pour réessayer."
    : disabled
      ? title
      : title;

  // Lien <a href> natif — navigation native navigateur, zéro window.open.
  // Désactivé via aria-disabled + pointer-events none + tabindex -1 pour
  // préserver l'arborescence tout en bloquant la nav.
  const commonLinkProps = {
    href: isReady ? href : undefined,
    target: "_blank",
    rel: "noopener noreferrer",
    "aria-disabled": !isReady,
    tabIndex: isReady ? 0 : -1,
    "data-testid": testid,
    title: tooltip,
    onClick: (e) => {
      if (!isReady) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
  };

  if (as === "button" || as === "button-outline") {
    const variant = as === "button" ? "default" : "outline";
    return (
      <Button
        asChild
        variant={variant}
        disabled={!isReady}
        className={className}
      >
        <a {...commonLinkProps}>
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            React.cloneElement(Icon, { className: "mr-2 h-4 w-4" })
          )}
          {displayLabel}
        </a>
      </Button>
    );
  }

  // Variante dropdown-item : rend un `<a>` stylé comme DropdownMenuItem.
  // Les callers l'enveloppent déjà dans un DropdownMenu/DropdownMenuContent.
  const itemBase =
    "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground";
  const itemDisabled = !isReady ? "pointer-events-none opacity-50 cursor-not-allowed" : "";
  return (
    <a
      {...commonLinkProps}
      className={`${itemBase} ${itemDisabled} ${className}`}
    >
      {loading ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        React.cloneElement(Icon, { className: "mr-2 h-4 w-4" })
      )}
      {displayLabel}
    </a>
  );
}

export default ViewPdfLink;
